import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import ExpirationPicker from "./fields/ExpirationPicker";
import LeveragePicker from "./fields/LeveragePicker";
import PricePickerDynamic from "./fields/PricePickerDynamic";
import TradeSidePicker from "./fields/TradeSidePicker";
import UnifiedPricePicker from "./fields/UnifiedPricePicker";
import EntryProtectionSelector from "./fields/EntryProtectionSelector";
import VolumePicker from "./fields/VolumePicker";
import { getTakeProfitOptions, saveTradedCurrency } from "../../../utils/utils";
import {
  buildUnifiedTradeCommand,
  formatTradeContextLabel,
  hasSelectedCandleContext,
  isSelectedCandlePriceSelector,
  TRADE_CANDLE_ENTRY_OPTIONS,
  TRADE_COMMENT_TEMPLATE,
  TRADE_DYNAMIC_ENTRY_OPTIONS,
  TRADE_ENTRY_SOURCES,
  TRADE_STOP_LOSS_OPTIONS
} from "../../../trades/tradeUtils";

function UnifiedTradeForm(props) {
  const {
    tradeSide,
    stopLoss,
    takeProfit,
    takeProfit2,
    setStopLoss,
    setTakeProfit,
    setTakeProfit2
  } = props;
  const [entrySource, setEntrySource] = useState(TRADE_ENTRY_SOURCES.ABSOLUTE_PRICE);
  const [entrySelector, setEntrySelector] = useState(null);
  const previousSide = useRef(tradeSide);
  const takeProfitOptions = useMemo(() => getTakeProfitOptions(), []);
  const hasCandle = hasSelectedCandleContext(props.tradeData?.selectedCandle);

  useEffect(() => {
    setEntrySource(props.tradeData?.entrySource || TRADE_ENTRY_SOURCES.ABSOLUTE_PRICE);
    setEntrySelector(props.tradeData?.entrySelector || null);
  }, [props.tradeData]);

  useEffect(() => {
    if (!tradeSide) return;
    if (!stopLoss) setStopLoss(TRADE_STOP_LOSS_OPTIONS[tradeSide][0]);
    if (!takeProfit) setTakeProfit(takeProfitOptions[tradeSide][0]);
    if (!takeProfit2) setTakeProfit2(takeProfitOptions[tradeSide][0]);
  }, [
    tradeSide,
    stopLoss,
    takeProfit,
    takeProfit2,
    setStopLoss,
    setTakeProfit,
    setTakeProfit2,
    takeProfitOptions
  ]);

  useEffect(() => {
    if (!hasCandle && entrySource === TRADE_ENTRY_SOURCES.SELECTED_CANDLE) {
      setEntrySource(TRADE_ENTRY_SOURCES.ABSOLUTE_PRICE);
      setEntrySelector(null);
    }
  }, [entrySource, hasCandle]);

  useEffect(() => {
    const oldSide = previousSide.current;
    if (!tradeSide || oldSide === tradeSide) return;
    if (!oldSide) {
      previousSide.current = tradeSide;
      return;
    }

    const oldStopOptions = TRADE_STOP_LOSS_OPTIONS[oldSide] || [];
    const newStopOptions = TRADE_STOP_LOSS_OPTIONS[tradeSide] || [];
    const stopIndex = oldStopOptions.indexOf(stopLoss);
    if (stopIndex >= 0) {
      setStopLoss(newStopOptions[stopIndex] || newStopOptions[0]);
    }

    if (!(TRADE_CANDLE_ENTRY_OPTIONS[tradeSide] || []).includes(entrySelector)
        && !TRADE_DYNAMIC_ENTRY_OPTIONS.includes(entrySelector)) {
      setEntrySource(TRADE_ENTRY_SOURCES.ABSOLUTE_PRICE);
      setEntrySelector(null);
    }
    previousSide.current = tradeSide;
  }, [entrySelector, setStopLoss, stopLoss, tradeSide]);

  const contextLabel = formatTradeContextLabel({
    currency: props.currency,
    interval: props.interval,
    candleLabel: props.candle,
    selectedPrice: props.price
  });

  const selectManualPrice = (value) => {
    props.setPrice(value);
    setEntrySource(TRADE_ENTRY_SOURCES.ABSOLUTE_PRICE);
    setEntrySelector(null);
  };

  const selectEntryOption = (option) => {
    setEntrySelector(option);
    if (TRADE_DYNAMIC_ENTRY_OPTIONS.includes(option)) {
      setEntrySource(TRADE_ENTRY_SOURCES.DYNAMIC);
    } else {
      setEntrySource(option === "MARKET"
        ? TRADE_ENTRY_SOURCES.ABSOLUTE_PRICE
        : TRADE_ENTRY_SOURCES.SELECTED_CANDLE);
    }
  };

  const handleSubmit = () => {
    const entryValue = entrySelector || props.price;
    const command = buildUnifiedTradeCommand({
      currency: props.currency,
      side: props.tradeSide,
      amountUsdt: props.volume,
      leverage: props.leverage,
      interval: props.interval,
      entry: { source: entrySource, value: entryValue },
      stopLoss: props.stopLoss,
      tp1: props.takeProfit,
      tp2: props.takeProfit2,
      expiresIn: props.expiresIn,
      entryProtectionMode: props.entryProtectionMode,
      comment: props.comment,
      selectedCandle: props.tradeData?.selectedCandle
    });

    if (!command) {
      alert("Side, amount, leverage and a valid entry source must be selected.");
      return;
    }

    props.handleSubmit(command);
    saveTradedCurrency(props.currency, props.tradeSide);
  };

  const candleOptionDisabled = (option) => (
    !TRADE_DYNAMIC_ENTRY_OPTIONS.includes(option) && !["PRICE", "MARKET"].includes(option) && !hasCandle
  );
  const stopOptionDisabled = (option) => isSelectedCandlePriceSelector(option) && !hasCandle;

  return (
    <Form className="pt-2 pb-2 details">
      <Row>
        <Col md={12}>
          <Form.Group className="mb-3 details">
            <h4 style={{ color: "white" }}>{contextLabel}</h4>
          </Form.Group>
        </Col>

        <Col md={12}>
          <TradeSidePicker tradeSide={props.tradeSide} setTradeSide={props.setTradeSide} />
        </Col>
        <Col md={12}>
          <VolumePicker volume={props.volume} setVolume={props.setVolume} />
        </Col>
        <Col md={12}>
          <LeveragePicker leverage={props.leverage} setLeverage={props.setLeverage} />
        </Col>

        <UnifiedPricePicker
          fieldLabel="Entry"
          manualValue={props.price}
          manualOptionLabel="PRICE"
          selectedOption={entrySelector}
          optionGroups={[
            {
              label: "Fixed / manual",
              options: ["MARKET"]
            },
            {
              label: "Selected candle",
              options: (TRADE_CANDLE_ENTRY_OPTIONS[props.tradeSide] || [])
                .filter((option) => option !== "MARKET")
            },
            {
              label: "Tracking BB / MA",
              options: TRADE_DYNAMIC_ENTRY_OPTIONS
            }
          ]}
          isOptionDisabled={candleOptionDisabled}
          onManualChange={selectManualPrice}
          onOptionSelect={selectEntryOption}
          currency={props.currency}
          interval={props.interval}
          draw={props.draw}
          setBtnDisabled={props.setBtnDisabled}
        />

        <Col md={12}>
          <EntryProtectionSelector
            value={props.entryProtectionMode}
            onChange={props.setEntryProtectionMode}
          />
        </Col>

        <PricePickerDynamic
          colSize={6}
          tradeSide={props.tradeSide}
          currency={props.currency}
          interval={props.interval}
          draw={props.draw}
          isPricePicker={true}
          setBtnDisabled={props.setBtnDisabled}
          fieldLabel="StopLoss"
          fieldValue={props.stopLoss}
          picklistOptions={TRADE_STOP_LOSS_OPTIONS}
          isOptionDisabled={stopOptionDisabled}
          disabledOptionTitle={() => "Select a candle to use this stop"}
          priceChangeCallback={props.setStopLoss}
        />

        <PricePickerDynamic
          colSize={6}
          tradeSide={props.tradeSide}
          currency={props.currency}
          interval={props.interval}
          draw={props.draw}
          isPricePicker={true}
          setBtnDisabled={props.setBtnDisabled}
          fieldLabel="TakeProfit 1"
          fieldValue={props.takeProfit}
          picklistOptions={takeProfitOptions}
          priceChangeCallback={props.setTakeProfit}
        />

        <PricePickerDynamic
          colSize={6}
          tradeSide={props.tradeSide}
          currency={props.currency}
          interval={props.interval}
          draw={props.draw}
          isPricePicker={true}
          setBtnDisabled={props.setBtnDisabled}
          fieldLabel="TakeProfit 2"
          fieldValue={props.takeProfit2}
          picklistOptions={takeProfitOptions}
          priceChangeCallback={props.setTakeProfit2}
        />

        <Col md={12}>
          <ExpirationPicker expiresIn={props.expiresIn} setExpiresIn={props.setExpiresIn} />
        </Col>

        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Journal</Form.Label>
            <Form.Control
              as="textarea"
              rows={7}
              value={props.comment || ""}
              placeholder={TRADE_COMMENT_TEMPLATE}
              onChange={(event) => props.setComment(event.target.value)}
            />
          </Form.Group>
        </Col>
      </Row>

      <Button className="w-100" variant="primary" onClick={handleSubmit}>Submit</Button>
    </Form>
  );
}

export default UnifiedTradeForm;
